import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios'; // Import axios for making HTTP requests
import { motion } from 'framer-motion';
import { FiUser, FiLock, FiLogIn, FiShield, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import actLogo from '../assets/actlogo.png';
import { endSession, storeSession } from '../services/session';

const AdminLoginPage = ({ setIsAuthenticated }) => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const navigate = useNavigate();

  // Session timeout (10 minutes)
  const sessionTimeout = 10 * 60 * 1000; // 10 minutes in milliseconds

  // Mount effect - sets isMounted flag after component is in DOM
  useEffect(() => {
    requestAnimationFrame(() => {
      setIsMounted(true);
    });
  }, []);

  useEffect(() => {
    const handleLogout = async () => {
      setIsAuthenticated(false);
      await endSession();
      navigate('/admin-login');
    };

    let logoutTimer;

    const resetTimer = () => {
      clearTimeout(logoutTimer);
      logoutTimer = setTimeout(() => {
        void handleLogout();
      }, sessionTimeout);
    };

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);
    resetTimer(); // Start the timer on page load

    return () => {
      clearTimeout(logoutTimer);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
    };
  }, [navigate, setIsAuthenticated, sessionTimeout]); // Added sessionTimeout to the dependency array

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setErrorDetails('');
    
    try {
      const response = await axios.post('/api/xapi', {
        action: 'login',
        email: usernameOrEmail, // Send usernameOrEmail as 'email'
        password: password,
        isAdmin: true,
      });

      if (response.data.message === 'Login successful') {
        setIsAuthenticated(true);
        storeSession(response.data.user, response.data.session);
        navigate('/admin');
      } else if (response.data.message === 'Error 789: Unauthorized access') {
        setError('Error 789: Unauthorized access');
        setErrorDetails('This account does not have admin privileges.');
      } else {
        setError('Invalid credentials');
        setErrorDetails('Please check your username and password and try again.');
      }
    } catch (error) {
      console.error('Login failed:', error.response ? error.response.data.message : error.message);
      
      // More specific error messages based on error type
      if (error.response) {
        if (error.response.status === 404) {
          setError('Admin not found');
          setErrorDetails('The email you entered doesn\'t exist in our system.');
        } else if (error.response.status === 401) {
          setError('Incorrect password');
          setErrorDetails('The password you entered is incorrect. Please contact support for admin access issues.');
        } else if (error.response.status === 403) {
          setError('Access denied');
          setErrorDetails('This account does not have admin privileges.');
        } else {
          setError('Login failed');
          setErrorDetails('Please check your credentials and try again.');
        }
      } else {
        setError('Connection error');
        setErrorDetails('Could not connect to the server. Please check your internet connection and try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminPasswordReset = async () => {
    if (!usernameOrEmail) {
      setError('Email required');
      setErrorDetails('Please enter your admin email address first.');
      setResetMessage('');
      return;
    }
    
    setIsResetting(true);
    setError('');
    setErrorDetails('');
    
    try {
      const response = await axios.post('/api/xapi', {
        action: 'requestAdminPasswordReset',
        email: usernameOrEmail,
      });
      
      if (response.data && response.data.message) {
        setResetMessage('If your email is a valid admin account, you will receive password reset instructions.');
      }
    } catch (error) {
      console.error('Failed to request admin password reset:', error);
      setError('Failed to request password reset');
      setErrorDetails('There was a problem processing your request. Please contact system support.');
      setResetMessage('');
    } finally {
      setIsResetting(false);
    }
  };

  // Show loading state until component is properly mounted
  if (!isMounted) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-brandDark to-brandBlue p-4">
        <div className="text-center">
          <div className="spinner mb-3 mx-auto" style={{ borderTopColor: 'white' }}></div>
          <p className="text-white">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-brandDark to-brandBlue p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white p-6 md:p-8 rounded-2xl shadow-card w-full max-w-md"
      >
        <div className="text-center mb-6 md:mb-8">
          <motion.img 
            src={actLogo} 
            alt="ACT Logo" 
            className="mx-auto mb-6 w-24 md:w-32"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.3 }}
          />
          <motion.div 
            className="flex items-center justify-center mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <FiShield className="text-brandDark mr-2 text-xl md:text-2xl" />
            <h2 className="text-2xl md:text-3xl font-bold text-brandDark font-heading">Admin Access</h2>
          </motion.div>
          <motion.p
            className="text-gray-600 text-sm md:text-base"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            Restricted area for authorized personnel only
          </motion.p>
        </div>
        
        {error && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-red-50 border-l-4 border-danger p-4 mb-6 rounded-md flex flex-col"
          >
            <div className="flex items-center">
              <FiAlertCircle className="text-danger mr-2 flex-shrink-0" />
              <span className="text-danger font-medium">{error}</span>
            </div>
            {errorDetails && (
              <span className="text-danger text-sm mt-1 ml-6">{errorDetails}</span>
            )}
          </motion.div>
        )}

        {resetMessage && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-green-50 border-l-4 border-success p-4 mb-6 rounded-md"
          >
            <span className="text-success">{resetMessage}</span>
          </motion.div>
        )}

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Admin Username or Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiUser className="text-gray-400" />
              </div>
              <input 
                type="text" 
                placeholder="Enter admin username or email" 
                value={usernameOrEmail} 
                onChange={(e) => setUsernameOrEmail(e.target.value)} 
                className="form-input pl-10 w-full"
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiLock className="text-gray-400" />
              </div>
              <input 
                type="password" 
                placeholder="Enter admin password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="form-input pl-10 w-full"
                required
              />
            </div>
          </div>

          <motion.button 
            type="submit"
            className="btn btn-primary w-full flex items-center justify-center"
            whileTap={{ scale: 0.98 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="spinner mr-2"></div>
            ) : (
              <FiLogIn className="mr-2" />
            )}
            {isLoading ? 'Authenticating...' : 'Admin Login'}
          </motion.button>
        </form>
        
        <div className="mt-4 text-center">
          <motion.button 
            onClick={handleAdminPasswordReset}
            className="text-brandBlue hover:underline text-sm"
            whileHover={{ scale: 1.05 }}
            disabled={isResetting}
          >
            {isResetting ? (
              <>
                <FiRefreshCw className="inline mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              'Forgot admin password?'
            )}
          </motion.button>
        </div>
        
        <motion.div 
          className="mt-6 text-center text-sm text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          Session automatically expires after 10 minutes of inactivity
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
